import {cn} from '@/lib/utils'

type SegmentedFilterBarSize = 'xs' | 'sm'
type SegmentedFilterBarShape = 'all' | 'left'

export interface SegmentedFilterOption<T extends string> {
    value: T
    label: string
    testId?: string
}

interface SegmentedFilterBarProps<T extends string> {
    options: ReadonlyArray<SegmentedFilterOption<T>>
    value: T
    onChange: (value: T) => void
    /** Button padding. `xs` = `px-2 py-1`, `sm` = `px-3 py-1`. Default: `sm`. */
    size?: SegmentedFilterBarSize
    /** Container shape. `all` = `rounded-lg`, `left` = `rounded-l-md border-r-0` (to abut an input). Default: `all`. */
    shape?: SegmentedFilterBarShape
    'data-testid'?: string
}

/* eslint-disable i18next/no-literal-string -- CSS class constants */
const shapeClasses: Record<SegmentedFilterBarShape, string> = {
    all: 'rounded-lg border',
    left: 'rounded-l-md border border-r-0',
}
const paddingClasses: Record<SegmentedFilterBarSize, string> = {
    xs: 'px-2 py-1',
    sm: 'px-3 py-1',
}
const activeButtonClass = 'bg-background text-foreground shadow-sm'
const inactiveButtonClass = 'text-muted-foreground hover:text-foreground'
/* eslint-enable i18next/no-literal-string */

/**
 * Horizontal segmented filter bar with muted surface and shadow-on-select.
 * Used for quick-pick filters like type (all/global/personal) or search scope (all/categories/reports).
 */
export function SegmentedFilterBar<T extends string>({
    options,
    value,
    onChange,
    size = 'sm',
    shape = 'all',
    'data-testid': testId,
}: SegmentedFilterBarProps<T>) {
    return (
        <div
            className={cn('bg-muted p-0.5 flex', shapeClasses[shape])}
            data-testid={testId}
        >
            {options.map((option) => {
                const isActive = option.value === value
                return (
                    <button
                        key={option.value}
                        type="button"
                        data-testid={option.testId}
                        onClick={() => onChange(option.value)}
                        className={cn(
                            'text-xs rounded-md transition-colors',
                            paddingClasses[size],
                            isActive ? activeButtonClass : inactiveButtonClass,
                        )}
                    >
                        {option.label}
                    </button>
                )
            })}
        </div>
    )
}
